import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AxiosError } from "axios";
import axios from "axios";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import {
  ButtonContainer,
  TertiaryButtonMediumV2,
  PrimaryButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import { SearchSelectInfiniteScroll } from "../../../../components/SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { endpoints } from "../../../../endpoints";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { useStoreState } from "../../../../util/util";
import { Form } from "../../../../layout/FormLayout";
import type {
  PIMProductOwnerUpdateArgs,
  User,
  Product,
} from "../../../../types/types";
import { zodSelectBoxDefault } from "../../../../util/zod.util";
import type { ProductMutate } from "../ProductDetailPage";

const ProductOwnerSchema = (t: (s: string) => string) =>
  z.object({
    product_owner: zodSelectBoxDefault(t),
  });

type FormOutput = z.infer<ReturnType<typeof ProductOwnerSchema>>;

type ProductOwnerSelectorProps = {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  mutateProduct: ProductMutate;
};

export const ProductOwnerSelector = ({
  isOpen,
  onClose,
  product,
  mutateProduct,
}: ProductOwnerSelectorProps) => {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();
  const { tenant_id, storefront_id } = useStoreState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentOwner = product?.product_owner;

  const { handleSubmit, control, formState, reset } = useForm<FormOutput>({
    resolver: zodResolver(ProductOwnerSchema(t)),
    defaultValues: {
      product_owner: currentOwner
        ? {
            label: `${currentOwner.firstname} ${currentOwner.lastname}`,
            value: currentOwner.id,
          }
        : { label: "", value: "" },
    },
  });

  useEffect(() => {
    const owner = product?.product_owner;
    reset({
      product_owner: owner
        ? {
            label: `${owner.firstname} ${owner.lastname}`,
            value: owner.id,
          }
        : { label: "", value: "" },
    });
  }, [product, isOpen, reset]);

  const onSubmit = async (values: FormOutput) => {
    setIsSubmitting(true);
    try {
      const payload: PIMProductOwnerUpdateArgs = {
        product_owner_id: values.product_owner.value,
      };
      await axios.patch<Product>(
        endpoints.v2_storefronts_id_pim_products_id(storefront_id, product.id),
        payload
      );
      await mutateProduct();
      notifySuccess(t("Product owner updated successfully"));
      onClose();
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessage =
        axiosError?.response?.data?.message ||
        t("Failed to update product owner");
      notifyError(errorMessage, { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prepare params object directly
  const userSearchParams = new URLSearchParams();
  userSearchParams.append("order_by", "asc");

  return (
    <SlideOut
      title={t("Assign Product Owner")}
      show={isOpen}
      closeFlyout={onClose}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="product_owner"
          render={(props) => (
            <SearchSelectInfiniteScroll
              {...props}
              label={t("Product Owner")}
              placeholder={t("Select User")}
              errors={formState.errors}
              formState={formState}
              baseUrl={endpoints.v1_storefronts_id_tenants_id_users(
                storefront_id,
                tenant_id
              )}
              params={userSearchParams}
              getOptions={(response: User[]) =>
                response.map((user) => ({
                  value: user.id,
                  label: `${user.firstname} ${user.lastname}`,
                }))
              }
            />
          )}
        />{" "}
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonMediumV2 style={{ width: "100%" }} onClick={onClose}>
            {t("Cancel")}
          </TertiaryButtonMediumV2>
          <PrimaryButtonLargeV2 fullWidth loading={isSubmitting} type="submit">
            {t("Update Owner")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </SlideOut>
  );
};
